import React, { useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import moment from "moment";
import graphqlToUserModel from "shared/util/graphqlToUserModel";
import noop from "shared/util/noop";

import withMessenger from "apps/messenger/containers/withMessenger";

import UserThumb from "shared/components/UserThumb";
import OnlineDot from "shared/components/OnlineDot";
import BoostBolt from "shared/components/BoostBolt";

import ReadStatus from "./ReadStatus";

import { getMessageOpener } from "../util/helpers";
import { OKCUPID_SYSTEM_USERID, ALICE_USERID } from "../util/constants";

import "./MessageRow.scss";

const MessageRow = ({
	correspondent,
	threadid,
	isUnread,
	sentTime,
	receivedTime,
	snippet,
	timestamp,
	inboxSize,
	isPlaceholder,
	markMessageRead,
	picBorderWidth,
	openConversation,
	onClick,
}) => {
	const [showUnreadIcon, setShowUnreadIcon] = useState(isUnread);

	if (isPlaceholder) {
		return (
			<div className="messages-messagerow messages-messagerow--placeholder">
				<div className="messages-messagerow-photo" />
				<div className="messages-messagerow-main">
					<div className="messages-messagerow-main-top-line">
						<div className="messages-messagerow-main-top-line-aboutuser">
							<span className="messages-messagerow-main-top-line-aboutuser-username" />
						</div>
					</div>
					<div className="messages-messagerow-main-snippet" />
				</div>
			</div>
		);
	}

	const { user } = correspondent;
	const isTwoWay = !!(sentTime && receivedTime);
	const showReplyIcon = isTwoWay && sentTime > receivedTime;

	const handleClick = () => {
		setShowUnreadIcon(false);
		onClick();
	};

	return (
		<button
			onClick={getMessageOpener({
				user: graphqlToUserModel(correspondent),
				onClick: handleClick,
				timestamp,
				inboxSize,
				isUnread,
				isTwoWay,
				threadid,
				markMessageRead,
				openConversation,
			})}
			className="messages-messagerow"
			aria-label={`Message from ${user.displayname}`}
		>
			<div className="messages-messagerow-status">
				<ReadStatus
					isUnread={showUnreadIcon}
					hasReplied={showReplyIcon}
				/>
			</div>

			<div className="messages-messagerow-photo">
				{user.username &&
					<UserThumb
						{...graphqlToUserModel(correspondent)}
						isAlice={user.userid === ALICE_USERID}
						username={user.displayname}
						indicatorWidth={picBorderWidth}
						shouldDisableAnimationMessages
					/>
				}
			</div>

			<div className="messages-messagerow-main">
				<div className="messages-messagerow-main-top-line">
					<div className="messages-messagerow-main-top-line-aboutuser">
						<span className="messages-messagerow-main-top-line-aboutuser-username">
							{user.displayname}
							{user.age && user.id !== OKCUPID_SYSTEM_USERID ? `, ${user.age}` : ""}
						</span>

						{user.isOnline &&
							<OnlineDot className="messages-messagerow-main-top-line-aboutuser-onlinestatus-dot" />
						}
						{!!(user.likes && user.likes.via_spotlight) &&
							<BoostBolt className="messages-messagerow-main-top-line-aboutuser-viaBoost-dot" />
						}
					</div>

					<div className="messages-messagerow-main-top-line-timestamp">
						{moment(timestamp).fromNow()}
					</div>
				</div>

				<div
					className={classnames({
						"messages-messagerow-main-snippet": true,
						"messages-messagerow-main-snippet--reply": showReplyIcon,
					})}
					dangerouslySetInnerHTML={{ __html: snippet }}
				/>
			</div>

		</button>
	);
};

MessageRow.propTypes = {
	isPlaceholder: PropTypes.bool,
	snippet: PropTypes.node,
	timestamp: PropTypes.number,

	threadid: PropTypes.string,
	isUnread: PropTypes.bool,
	sentTime: PropTypes.number,
	receivedTime: PropTypes.number,
	correspondent: PropTypes.object,
	inboxSize: PropTypes.number,
	markMessageRead: PropTypes.func,

	picBorderWidth: PropTypes.number,
	onClick: PropTypes.func,

	openConversation: PropTypes.func,
};

MessageRow.defaultProps = {
	isPlaceholder: false,
	snippet: null,
	timestamp: null,
	threadid: null,
	isUnread: null,
	sentTime: 0,
	receivedTime: 0,
	correspondent: {},
	inboxSize: null,
	picBorderWidth: 3,
	markMessageRead: noop,
	openConversation: noop,
	onClick: noop,
};

export default withMessenger(MessageRow);



// WEBPACK FOOTER //
// ./src/apps/messages/components/MessageRow.jsx